import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DEFAULT_SITE } from '../siteDefaults';
import { resolveFounderDesk } from '../siteFounderDesk';
import { fetchSite, getToken, patchFounderDesk, uploadFile } from './api';
import { AdminImagePreview, AdminModal, fileInput, input, label } from './adminUi';

const cardClass = 'rounded-xl border border-neutral-200 bg-white p-4 shadow-sm sm:p-5';
const primaryBtnClass =
  'rounded-lg bg-brand-olive px-3 py-2 text-sm font-semibold text-white hover:opacity-95 active:opacity-90 disabled:opacity-50';
const secondaryBtnClass =
  'rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm font-medium text-neutral-800 hover:bg-neutral-50';

function ModalFooter({ onCancel, onApply }) {
  return (
    <div className="flex flex-wrap justify-end gap-2">
      <button type="button" onClick={onCancel} className={secondaryBtnClass}>
        Cancel
      </button>
      <button type="button" onClick={onApply} className={primaryBtnClass}>
        Apply
      </button>
    </div>
  );
}

function clip(s, n) {
  if (typeof s !== 'string' || !s.trim()) return '—';
  const t = s.trim();
  return t.length <= n ? t : `${t.slice(0, n)}…`;
}

function founderDraftFromForm(form) {
  return {
    sectionLabel: form.sectionLabel || '',
    name: form.name || '',
    role: form.role || '',
    message1: form.message1 || '',
    message2: form.message2 || '',
    message3: form.message3 || '',
    ctaLabel: form.ctaLabel || '',
    ctaHref: form.ctaHref || '',
    portraitUrl: form.portraitUrl || '',
    portraitAlt: form.portraitAlt || '',
  };
}

export default function FounderDeskDashboard() {
  const navigate = useNavigate();
  const [form, setForm] = useState(() => resolveFounderDesk(DEFAULT_SITE));
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploadingPortrait, setUploadingPortrait] = useState(false);

  const [modalErr, setModalErr] = useState('');
  const [editorOpen, setEditorOpen] = useState(false);
  const [draft, setDraft] = useState(null);

  useEffect(() => {
    if (!getToken()) {
      navigate('/admin', { replace: true });
      return;
    }
    fetchSite()
      .then((site) => setForm(resolveFounderDesk(site)))
      .catch(() => setForm(resolveFounderDesk(DEFAULT_SITE)));
  }, [navigate]);

  function openEditor() {
    setModalErr('');
    setDraft(founderDraftFromForm(form));
    setEditorOpen(true);
  }

  function closeEditor() {
    setEditorOpen(false);
    setDraft(null);
    setModalErr('');
  }

  function applyEditor() {
    if (!draft) return;
    setForm((f) => ({
      ...f,
      ...draft,
    }));
    closeEditor();
  }

  async function onPortraitUploadInModal(file) {
    if (!file) return;
    setErr('');
    setUploadingPortrait(true);
    try {
      const { url } = await uploadFile(file);
      setModalErr('');
      setDraft((d) => (d ? { ...d, portraitUrl: url } : d));
    } catch (e) {
      const m = e.message || 'Upload failed';
      setErr(m);
      setModalErr(m);
    } finally {
      setUploadingPortrait(false);
    }
  }

  async function save() {
    setMsg('');
    setErr('');
    setSaving(true);
    try {
      const payload = {
        sectionLabel: form.sectionLabel,
        name: form.name,
        role: form.role,
        message1: form.message1,
        message2: form.message2,
        message3: form.message3,
        ctaLabel: form.ctaLabel,
        ctaHref: form.ctaHref,
        portraitUrl: form.portraitUrl,
        portraitAlt: form.portraitAlt,
      };
      const { founderDesk } = await patchFounderDesk(payload);
      setForm(resolveFounderDesk({ founderDesk: founderDesk || payload }));
      setMsg('Saved.');
    } catch (e) {
      setErr(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <header className="z-10 flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-neutral-200 bg-white px-4 py-3 md:px-8">
        <div className="min-w-0 flex-1">
          <h1 className="text-lg font-bold text-neutral-900">Founder&apos;s Desk</h1>
          <p className="text-sm text-neutral-600">Portrait, copy, and CTA — edit in dialog, then save to publish</p>
        </div>
        <button type="button" disabled={saving} onClick={save} className={primaryBtnClass}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </header>

      <div className="min-h-0 w-full min-w-0 flex-1 overflow-y-auto px-4 py-6 md:px-8">
        {msg ? (
          <p className="mb-4 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
            {msg}
          </p>
        ) : null}
        {err ? (
          <p className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{err}</p>
        ) : null}

        <section className={cardClass}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="m-0 text-xs font-semibold uppercase tracking-wide text-neutral-500">{clip(form.sectionLabel, 40)}</p>
              <h2 className="mt-1 text-lg font-bold text-neutral-900">{clip(form.name, 48)}</h2>
              <p className="mt-1 text-sm text-neutral-600">{clip(form.role, 72)}</p>
              <p className="mt-3 text-sm text-neutral-700">
                {clip(form.message1, 140)}
              </p>
              <p className="mt-2 text-xs text-neutral-500">
                CTA: {clip(form.ctaLabel, 24)} → {clip(form.ctaHref, 36)}
              </p>
            </div>
            <button type="button" onClick={openEditor} className={secondaryBtnClass}>
              Edit in dialog
            </button>
          </div>
          {form.portraitUrl ? (
            <div className="mt-4 flex max-w-xs items-start gap-3">
              <img
                src={form.portraitUrl}
                alt={form.portraitAlt || ''}
                className="h-24 w-20 shrink-0 rounded-lg object-cover object-top ring-1 ring-neutral-200"
              />
              <p className="m-0 text-xs text-neutral-500">Portrait set · open the dialog to replace or clear.</p>
            </div>
          ) : (
            <p className="mt-4 text-sm text-neutral-500">No portrait yet. Open the dialog to add an image.</p>
          )}
        </section>
      </div>

      <AdminModal
        open={editorOpen}
        title="Founder&apos;s Desk"
        description="Section label, founder details, three message blocks, CTA, and portrait."
        error={modalErr}
        onClose={closeEditor}
        footer={<ModalFooter onCancel={closeEditor} onApply={applyEditor} />}
      >
        {draft ? (
          <div className="grid gap-4 md:grid-cols-2">
            <label className={label}>
              Section label
              <input
                className={input}
                value={draft.sectionLabel}
                onChange={(e) => setDraft((d) => ({ ...d, sectionLabel: e.target.value }))}
              />
            </label>
            <label className={label}>
              Founder name
              <input className={input} value={draft.name} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} />
            </label>
            <label className={`${label} md:col-span-2`}>
              Role / title
              <input className={input} value={draft.role} onChange={(e) => setDraft((d) => ({ ...d, role: e.target.value }))} />
            </label>
            <label className={label}>
              CTA label
              <input className={input} value={draft.ctaLabel} onChange={(e) => setDraft((d) => ({ ...d, ctaLabel: e.target.value }))} />
            </label>
            <label className={label}>
              CTA href
              <input className={input} value={draft.ctaHref} onChange={(e) => setDraft((d) => ({ ...d, ctaHref: e.target.value }))} />
            </label>
            <label className={`${label} md:col-span-2`}>
              Portrait alt text
              <input className={input} value={draft.portraitAlt} onChange={(e) => setDraft((d) => ({ ...d, portraitAlt: e.target.value }))} />
            </label>

            <label className={`${label} md:col-span-2`}>
              Message paragraph 1
              <textarea
                className={`${input} min-h-[5.5rem]`}
                value={draft.message1}
                onChange={(e) => setDraft((d) => ({ ...d, message1: e.target.value }))}
              />
            </label>
            <label className={`${label} md:col-span-2`}>
              Message paragraph 2
              <textarea
                className={`${input} min-h-[5.5rem]`}
                value={draft.message2}
                onChange={(e) => setDraft((d) => ({ ...d, message2: e.target.value }))}
              />
            </label>
            <label className={`${label} md:col-span-2`}>
              Message paragraph 3
              <textarea
                className={`${input} min-h-[5.5rem]`}
                value={draft.message3}
                onChange={(e) => setDraft((d) => ({ ...d, message3: e.target.value }))}
              />
            </label>

            <label className={`${label} md:col-span-2`}>
              Portrait image URL
              <input
                className={input}
                value={draft.portraitUrl}
                onChange={(e) => {
                  setModalErr('');
                  setDraft((d) => ({ ...d, portraitUrl: e.target.value }));
                }}
              />
            </label>
            <label className={`${label} md:col-span-2`}>
              Upload portrait
              <input
                type="file"
                accept="image/*"
                disabled={uploadingPortrait}
                className={fileInput}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  onPortraitUploadInModal(f);
                }}
              />
            </label>
            {uploadingPortrait ? <p className="text-xs text-neutral-500 md:col-span-2">Uploading…</p> : null}
            <div className="md:col-span-2">
              <AdminImagePreview src={draft.portraitUrl} alt={draft.portraitAlt || ''} emptyLabel="No portrait yet" />
            </div>
            {draft.portraitUrl ? (
              <div className="md:col-span-2">
                <button
                  type="button"
                  onClick={() => setDraft((d) => (d ? { ...d, portraitUrl: '' } : d))}
                  className="text-xs font-medium text-neutral-600 underline decoration-neutral-300 hover:text-neutral-900"
                >
                  Clear portrait URL
                </button>
              </div>
            ) : null}
          </div>
        ) : null}
      </AdminModal>
    </>
  );
}
